import { Shop } from '@@types'
import classNames from 'classnames'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useShopTime from '@shared/hooks/useShopTime'
import Button from '@shared/ui/button'

import { calculateDistance, formatDistance } from '../lib/distance'

import styles from './ShopsPage.module.scss'


export interface ShopDetailsProps {
  shop: Shop
  onClose: () => void
  userLocation?: { lat: number; lng: number } | null
  onShowOnMap?: () => void
}

export const ShopDetails = ({ shop, onClose, userLocation, onShowOnMap }: ShopDetailsProps) => {
  const { t: ts } = useTranslation('shops')
  const [ currentImageIndex, setCurrentImageIndex ] = useState(0)
  const { open, close, getShopStatus } = useShopTime(
    shop?.open_time || 0,
    shop?.close_time || 0,
  )

  const distance = useMemo(() => {
    if (!userLocation || !shop?.shopGeoPoint?.latitude || !shop?.shopGeoPoint?.longitude) {
      return null
    }
    return calculateDistance(
      userLocation.lat,
      userLocation.lng,
      shop.shopGeoPoint.latitude,
      shop.shopGeoPoint.longitude
    )
  }, [ userLocation, shop?.shopGeoPoint ])

  const handleRoute = () => {
    const { latitude, longitude } = shop.shopGeoPoint || {}
    if (latitude && longitude) {
      const url = `https://yandex.ru/maps/?rtext=~${latitude},${longitude}&rtt=auto`
      window.open(url, '_blank')
    }
  }

  const images = shop.images || []
  const hasImages = images.length > 0

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))
  }

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))
  }

  return (
    <div className={styles.shopDetails}>
      {/* Header */}
      <button className={styles.shopDetails__back} onClick={onClose}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        {ts('back_to_list')}
      </button>

      {/* Image Carousel */}
      {hasImages && (
        <div className={styles.shopDetails__carousel}>
          <img 
            src={images[currentImageIndex]?.url} 
            alt={shop.name}
            className={styles.shopDetails__carouselImage}
          />
          {images.length > 1 && (
            <>
              <button 
                className={classNames(styles.shopDetails__carouselBtn, styles.shopDetails__carouselBtn_prev)}
                onClick={handlePrevImage}
              >
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                  <path d="M7 1L1 7L7 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className={classNames(styles.shopDetails__carouselBtn, styles.shopDetails__carouselBtn_next)}
                onClick={handleNextImage}
              >
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                  <path d="M1 1L7 7L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </>
          )}
        </div>
      )}

      {/* Address + Distance */}
      <div className={styles.shopDetails__addressRow}>
        <h3 className={styles.shopDetails__address}>{shop.address}</h3>
        {distance !== null && (
          <span className={styles.shopDetails__distance}>{formatDistance(distance)}</span>
        )}
      </div>

      {/* Tags */}
      {shop.shopTags && shop.shopTags.length > 0 && (
        <div className={styles.shopDetails__tags}>
          {shop.shopTags.map((tag) => (
            <span key={tag.id} className={styles.shopDetails__tag}>
              {tag.icon?.url && (
                <img src={tag.icon.url} alt="" className={styles.shopDetails__tagIcon} />
              )}
              {tag.name}
            </span>
          ))}
        </div>
      )}

      {/* Schedule */}
      <div className={styles.shopDetails__schedule}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M10 5V10L13 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <div className={styles.shopDetails__scheduleInfo}>
          <span className={styles.shopDetails__scheduleText}>
            {ts('daily_hours', { open, close })}
          </span>
          <span 
            className={classNames(styles.shopDetails__status, {
              [styles.shopDetails__status_open]: getShopStatus,
              [styles.shopDetails__status_closed]: !getShopStatus,
            })}
          >
            {getShopStatus ? ts('status_open') : ts('status_closed')}
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className={styles.shopDetails__divider} />

      {/* Phone */}
      {shop.phone && (
        <a href={`tel:${shop.phone}`} className={styles.shopDetails__phone}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M18 14.5V17C18 17.55 17.55 18 17 18H16.5C9 18 3 12 3 4.5V4C3 3.45 3.45 3 4 3H6.5L8.5 7.5L6.5 9C7.37 10.87 8.88 12.38 10.75 13.25L12.25 11.25L16.75 13.25L18 14.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{shop.phone}</span>
        </a>
      )}

      {/* Show on Map Link */}
      {onShowOnMap && (
        <button className={styles.shopDetails__showOnMap} onClick={onShowOnMap}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 11C11.1046 11 12 10.1046 12 9C12 7.89543 11.1046 7 10 7C8.89543 7 8 7.89543 8 9C8 10.1046 8.89543 11 10 11Z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10 17C10 17 16 13 16 9C16 5.68629 13.3137 3 10 3C6.68629 3 4 5.68629 4 9C4 13 10 17 10 17Z" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
          {ts('show_on_map')}
        </button>
      )}

      {/* Buttons */}
      <div className={styles.shopDetails__buttons}>
        <Button theme="primary" onClick={handleRoute}>
          {ts('route')}
        </Button>
        {shop.isPickupDeliveryAvailable && (
          <Button theme="grey">
            {ts('select_pickup')}
          </Button>
        )}
      </div>
    </div>
  )
}
